import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  ButtonTheme,
  ButtonType,
  LoaderTheme,
  ModalButtons,
  NotificationButtonStyle,
  NotificationLayout,
  NotificationTheme,
  TabTheme,
} from '@fcom/ui-components';
import { ElementActions, ElementTypes } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { stopPropagation, unsubscribe } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import { GaContext } from '@fcom/common/interfaces/gtm.interface';
import { LoyaltyRoutesPath } from '@fcom/loyalty-core/interfaces';

import { TwoFactorAuthService, TwoFactorAuthStatus, TwoFactorAuthType } from '../../services/two-factor-auth.service';
import { ProfileService } from '../../services/profile.service';
import { AviosLinkingService, AviosAccountLinkingStatus } from '../../../loyalty-plus/services/avios-linking.service';

export enum AuthState {
  CHOOSE_TYPE,
  LOADING,
  VERIFY,
  INCORRECT_CODE,
  ERROR,
}

@Component({
  selector: 'fin-loyalty-two-factor-auth-toggle',
  templateUrl: 'loyalty-two-factor-auth-toggle.component.html',
  styleUrls: ['./loyalty-two-factor-auth-toggle.component.scss'],
})
export class LoyaltyTwoFactorAuthToggleComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly TabTheme = TabTheme;
  readonly TwoFactorAuthType = TwoFactorAuthType;
  readonly TwoFactorAuthStatus = TwoFactorAuthStatus;
  readonly AuthState = AuthState;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationButtonStyle = NotificationButtonStyle;
  readonly LoaderTheme = LoaderTheme;
  readonly LoyaltyRoutesPath = LoyaltyRoutesPath;
  readonly AviosAccountLinkingStatus = AviosAccountLinkingStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() enabledBackButtonUrl: string;

  qrCodeImageUrl$: Observable<string>;
  parsedMobilePhone$: Observable<string>;

  twoFactorAuthStatus$: Observable<TwoFactorAuthStatus>;
  aviosLinkingStatus$: Observable<AviosAccountLinkingStatus>;

  authState: AuthState = AuthState.CHOOSE_TYPE;
  secretKey: string;
  isConfirmModalOpen = false;
  isMFAModalOpened = false;
  hasError = false;

  validateGAuthForm: UntypedFormGroup;

  @ViewChild('fcomNotification', { static: false })
  notification: ElementRef;

  @ViewChild('appFirstStep', { static: false })
  appFirstStep: ElementRef;

  @ViewChild('appThirdStep', { static: false })
  appThirdStep: ElementRef;

  private subscriptions = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private twoFactorAuthService: TwoFactorAuthService,
    private gtmService: GtmService,
    private cdRef: ChangeDetectorRef,
    private profileService: ProfileService,
    private aviosLinkingService: AviosLinkingService
  ) {
    this.validateGAuthForm = this.fb.group({
      securityCode: [null, [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.twoFactorAuthStatus$ = this.twoFactorAuthService.status$;
    this.aviosLinkingStatus$ = this.aviosLinkingService.status$;
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  resetAuthState(): void {
    this.validateGAuthForm.reset();
    this.authState = AuthState.CHOOSE_TYPE;
    this.secretKey = undefined;
    this.cdRef.detectChanges();
    this.setFocus(this.appFirstStep);
  }

  onCloseError(): void {
    this.hasError = false;
  }

  selectTwoFactorAuthType(authType: string): void {
    if (authType === TwoFactorAuthType.SMS) {
      this.subscriptions.add(
        this.twoFactorAuthService.enableSMSAuth().subscribe({
          next: () => {
            this.trackGTM('activating-successful');
          },
          error: () => {
            this.hasError = true;
            this.trackGTM('activating-failed');
            this.cdRef.detectChanges();
            this.notification.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          },
        })
      );
    } else if (authType === TwoFactorAuthType.APP) {
      this.subscriptions.add(
        this.twoFactorAuthService.enableGAuthValidated().subscribe({
          next: (response) => {
            this.authState = AuthState.VERIFY;
            this.qrCodeImageUrl$ = this.twoFactorAuthService.getQRCodeImageUrl(response.secretKey);
            this.secretKey = response.secretKey;
            this.cdRef.detectChanges();
            this.setFocus(this.appThirdStep);
          },
          error: () => {
            this.authState = AuthState.CHOOSE_TYPE;
            this.hasError = true;
            this.cdRef.detectChanges();
            this.notification.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          },
        })
      );
    }
  }

  validateCode(): void {
    const securityCode = this.validateGAuthForm.get('securityCode').value;
    if (!securityCode || !/^\d{6}$/.test(securityCode)) {
      this.authState = AuthState.INCORRECT_CODE;
      return;
    }
    this.authState = AuthState.LOADING;
    this.subscriptions.add(
      this.twoFactorAuthService.validateGAuth(this.validateGAuthForm.get('securityCode').value).subscribe({
        next: (response) => {
          if (response.valid) {
            this.authState = AuthState.CHOOSE_TYPE;
            this.validateGAuthForm.reset();
            this.trackGTM('activating-successful');
          } else {
            this.authState = AuthState.INCORRECT_CODE;
          }
        },
        error: () => {
          this.authState = AuthState.ERROR;
          this.trackGTM('activating-failed');
        },
      })
    );
  }

  disableGAuth(): void {
    this.subscriptions.add(
      this.twoFactorAuthService.disableGAuth().subscribe({
        next: () => {
          this.authState = AuthState.CHOOSE_TYPE;
          this.trackGTM('disabling-successful');
        },
        error: () => {
          this.hasError = true;
          this.trackGTM('disabling-failed');
        },
      })
    );
  }

  openConfirmModal(event: Event): void {
    stopPropagation(event);
    this.isConfirmModalOpen = true;
    this.parsedMobilePhone$ = this.profileService.getProfile().pipe(
      filter(
        ({ parsedMobilePhone: { countryCode, nationalNumber } }) =>
          countryCode !== undefined && nationalNumber !== undefined
      ),
      map(({ parsedMobilePhone: { countryCode, nationalNumber } }) => `+${countryCode} ${nationalNumber}`),
      finShare()
    );
  }

  private trackGTM(state: string): void {
    this.gtmService.trackElement(
      `update-profile-2fa-${state}`,
      GaContext.SERVICE_FORMS,
      ElementTypes.MODAL,
      state,
      ElementActions.SUBMIT
    );
  }

  onBackButtonLinkClick(): void {
    this.gtmService.trackElement(
      'update-profile-2fa-back-button-url',
      GaContext.SERVICE_FORMS,
      ElementTypes.NOTIFICATION,
      undefined,
      ElementActions.CLICK
    );
  }

  private setFocus(target: ElementRef): void {
    target.nativeElement.focus();
  }
}
